"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { ChevronRight, ChevronLeft, Check, Calendar, Clock, MapPin, User, Mail, Phone, Hammer, FileText, Loader, Info } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { cn } from "@/lib/utils";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<"textarea">>(({
  className,
  ...props
}, ref) => {
  return <textarea className={cn("file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex min-h-[120px] w-full min-w-0 rounded-sm border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-sans", "focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-[3px]", "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive", className)} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
const Select = React.forwardRef<HTMLSelectElement, React.ComponentProps<"select">>(({
  className,
  ...props
}, ref) => {
  return <div data-section-id="8186" className="relative">
      <select className={cn("file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-10 w-full min-w-0 rounded-sm border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm appearance-none font-sans", "focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-[3px]", "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive", className)} ref={ref} {...props} />
      <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground rotate-90 pointer-events-none" />
    </div>;
});
Select.displayName = "Select";
export default function ScheduleYourEstimate() {
  return <section id="get-your-quote" className="bg-background text-foreground py-16 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-96 bg-secondary/40 -z-10" />
      
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="mb-12 text-center space-y-4">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary tracking-tight">
            Schedule Your Consultation
          </h2>
          <p className="text-muted-foreground font-sans text-lg max-w-2xl mx-auto">
            Ready to transform your home? Share your vision with Finely Done and let our 20+ years of expertise bring it to life.
          </p>
        </div>

        <div className="bg-card border border-border rounded-sm shadow-xl overflow-hidden">
          <FormProvider formId="schedule_estimate_estimate" sectionName="schedule-estimate" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          address: "",
          project_type: "",
          project_details: "",
          preferred_date: "",
          preferred_time: "Morning (8 am – 12 pm)"
        }} validationRules={{
          full_name: {
            required: "Full Name is required"
          },
          email: {
            required: "Email is required",
            pattern: "email"
          },
          phone: {
            required: "Phone Number is required",
            pattern: "phone"
          },
          address: {
            required: "Project Address is required"
          },
          project_type: {
            required: "Project Type is required"
          },
          preferred_date: {
            required: "Preferred Date is required"
          }
        }} submitText="Request Quote" successMessage="Thank you for choosing Finely Done. We've received your request and will contact you shortly to confirm your consultation." errorMessage="Something went wrong. Please call us directly at (555) FINELY-DONE." className="h-full">
            <MultiStepForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function MultiStepForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    trigger
  } = useFormContext();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const progress = step / totalSteps * 100;
  const nextStep = async () => {
    let fieldsToValidate: string[] = [];
    if (step === 1) {
      fieldsToValidate = ["full_name", "email", "phone", "address"];
    } else if (step === 2) {
      fieldsToValidate = ["project_type", "project_details"];
    }
    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setStep(prev => Math.min(prev + 1, totalSteps));
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  if (isSubmitted && !submitError) {
    return <div className="p-16 flex flex-col items-center justify-center text-center space-y-6 animate-in fade-in zoom-in duration-700">
        <div className="size-24 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
          <Check className="size-12" />
        </div>
        <h3 className="text-3xl font-serif font-bold text-primary">Request Received</h3>
        <p className="text-muted-foreground font-sans max-w-md text-lg">
          {successMessage}
        </p>
        <Button onClick={() => {
        resetForm();
        setStep(1);
      }} variant="outline" className="mt-6 rounded-sm border-primary text-primary hover:bg-primary/5">
          Schedule Another Project
        </Button>
      </div>;
  }
  return <div className="flex flex-col h-full">
      {}
      <div className="bg-muted/30 border-b border-border p-8">
        <div className="flex justify-between items-center mb-6 text-sm font-sans font-medium text-muted-foreground uppercase tracking-wider">
          <span>Step {step} of {totalSteps}</span>
          <span className="text-primary font-bold">
            {step === 1 && "Personal Information"}
            {step === 2 && "Remodeling Vision"}
            {step === 3 && "Scheduling"}
          </span>
        </div>
        <Progress value={progress} className="h-1.5 bg-border [&>div]:bg-primary" />
      </div>

      {}
      <Form onSubmit={handleSubmit} className="p-8 md:p-12 space-y-8 flex-1">
        
        {}
        {step === 1 && <div className="space-y-6 animate-in slide-in-from-right-8 fade-in duration-500">
            <div className="grid md:grid-cols-2 gap-8">
              <FormField control={control} name="full_name" render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-sans font-semibold text-primary">Full Name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                        <Input placeholder="Enter your full name" className="pl-9 rounded-sm border-input font-sans" {...field} />
                      </div>
                    </FormControl>
                    <FormDescription className="text-xs">As you would like it to appear on project documents.</FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="email" render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-sans font-semibold text-primary">Email Address</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                        <Input type="email" placeholder="email@example.com" className="pl-9 rounded-sm border-input font-sans" {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <FormField control={control} name="phone" render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-sans font-semibold text-primary">Phone Number</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Phone className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                        <Input type="tel" placeholder="(715) 000-0000" className="pl-9 rounded-sm border-input font-sans" {...field} />
                      </div>
                    </FormControl>
                    <FormDescription className="text-xs">We will call to confirm your consultation details.</FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="address" render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-sans font-semibold text-primary">Project Address</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                        <Input placeholder="Hudson, River Falls, or Prescott area" className="pl-9 rounded-sm border-input font-sans" {...field} />
                      </div>
                    </FormControl>
                    <FormDescription className="text-xs">The location where the remodeling will take place.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>}

        {}
        {step === 2 && <div className="space-y-6 animate-in slide-in-from-right-8 fade-in duration-500">
            <FormField control={control} name="project_type" render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-sans font-semibold text-primary">Project Category</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Hammer className="absolute left-3 top-3 size-4 text-muted-foreground z-10" />
                      <Select className="pl-9" {...field}>
                        <option value="" disabled>Select your primary project area</option>
                        {["Bathroom Remodeling (Primary)", "Kitchen Remodeling", "Basement Finishing", "Whole-Home Renovation", "Custom Carpentry", "Accessibility Upgrades", "Other Specialty Project"].map((opt, i) => <option key={i} value={opt} data-index={i}>{opt}</option>)}
                      </Select>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="project_details" render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-sans font-semibold text-primary">Tell Us About Your Vision</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <FileText className="absolute left-3 top-3 size-4 text-muted-foreground" />
                      <Textarea placeholder="Describe your goals (e.g., custom walk-in shower, luxury kitchen island, finished family room in the basement...)" className="pl-9 min-h-[160px] rounded-sm border-input font-sans" {...field} />
                    </div>
                  </FormControl>
                  <FormDescription className="text-xs">The more detail you provide, the better we can prepare for our first meeting.</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>}

        {}
        {step === 3 && <div className="space-y-6 animate-in slide-in-from-right-8 fade-in duration-500">
            <div className="grid md:grid-cols-2 gap-8">
              <FormField control={control} name="preferred_date" render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-sans font-semibold text-primary">Preferred Consultation Date</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <SafeHydrate><Calendar className="absolute left-3 top-2.5 size-4 text-muted-foreground" /></SafeHydrate>
                        <Input type="date" className="pl-9 rounded-sm border-input font-sans" {...field} />
                      </div>
                    </FormControl>
                    <FormDescription className="text-xs">Select a day that works best for your schedule.</FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="preferred_time" render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-sans font-semibold text-primary">Preferred Time Window</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Clock className="absolute left-3 top-3 size-4 text-muted-foreground z-10" />
                        <Select className="pl-9" {...field}>
                          {["Morning (8 am – 12 pm)", "Afternoon (12 pm – 4 pm)", "Evening (4 pm – 6 pm)"].map((opt, i) => <option key={i} value={opt} data-index={i}>{opt}</option>)}
                        </Select>
                      </div>
                    </FormControl>
                    <FormDescription className="text-xs">When should our estimator arrive?</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="bg-secondary/20 p-6 rounded-sm border border-secondary flex gap-4 items-start">
              <Info className="size-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm font-sans text-muted-foreground leading-relaxed">
                Finely Done values your time. We will do our absolute best to accommodate your requested time and will call you within one business day to finalize the appointment.
              </p>
            </div>
          </div>}

        {}
        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm animate-in fade-in">
            <p className="text-destructive text-sm font-sans font-medium flex items-center gap-2">
              <span className="text-lg"></span> {submitError}
            </p>
          </div>}

        {}
        <div className="flex justify-between pt-8 border-t border-border mt-10">
          <Button type="button" variant="ghost" onClick={prevStep} disabled={step === 1 || isSubmitting} className={cn(step === 1 && "invisible", "font-sans text-muted-foreground hover:text-primary")}>
            <ChevronLeft className="mr-2 size-4" />
            Previous
          </Button>

          {step < totalSteps ? <Button type="button" onClick={nextStep} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm px-8 font-sans font-bold shadow-md">
              Next Step
              <ChevronRight className="ml-2 size-4" />
            </Button> : <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm min-w-[180px] font-sans font-bold shadow-md">
              {isSubmitting ? <>
                  <Loader className="mr-2 size-4 animate-spin" />
                  Submitting...
                </> : <>
                  Request Your Quote
                  <Check className="ml-2 size-4" />
                </>}
            </Button>}
        </div>
      </Form>
    </div>;
}